import { useQuery } from '@tanstack/react-query'
import type { CardIconKey } from '@chillout/ui'
import { supabase } from '../../../lib/supabase'
import type { RawActivityTransaction } from '../../shared/mapTransactionToActivity'

export interface StampMilestone {
  stampPosition: number
  cardIcon: CardIconKey
}

export interface MemberRewardBalance {
  rewardItemId: string
  name: string
  count: number
  earliestExpiry: string | null
}

export interface CatalogRewardItem {
  id: string
  name: string
}

export interface AdminMemberDetail {
  id: string
  firstName: string | null
  lastName: string | null
  email: string
  createdAt: string
  currentStamps: number
  stampTarget: number
  maxStampsPerTransaction: number
  completionCardIcon: CardIconKey
  milestones: StampMilestone[]
  rewardBalance: MemberRewardBalance[]
  rewardCatalog: CatalogRewardItem[]
  recentActivity: RawActivityTransaction[]
}

interface RawTransaction {
  loyalty_config_snapshot: { earning_method?: string; amount_per_stamp?: number; trigger?: string; stamp_target_before?: number; stamp_target_after?: number; stamps_before?: number; stamps_after?: number } | null
}

function resolveFirst<T>(value: T | T[] | null | undefined): T | null {
  if (value == null) return null
  return Array.isArray(value) ? value[0] ?? null : value
}

interface RpcActivityRow {
  id: string
  type: string
  stamps_delta: number
  rewards_delta: number
  created_at: string
  actor_name: string | null
  actor_role: string | null
  receipt_id: string | null
  correction_reason: string | null
  correction_note: string | null
  device_name: string | null
  reward_item_name: Record<string, string> | null
  loyalty_config_snapshot: RawTransaction['loyalty_config_snapshot']
}

async function fetchMemberDetail(memberId: string, lang: string): Promise<AdminMemberDetail> {
  const [profileResult, stampCardResult, configResult, settingsResult, rewardsResult, catalogResult, txResult] = await Promise.all([
    supabase
      .from('member_profiles')
      .select('id, first_name, last_name, email, created_at')
      .eq('id', memberId)
      .single(),
    supabase
      .from('stamp_cards')
      .select('current_stamps')
      .eq('member_id', memberId)
      .single(),
    supabase
      .from('loyalty_config')
      .select('stamp_target, completion_card_icon, milestones')
      .eq('status', 'active')
      .single(),
    supabase
      .from('app_settings')
      .select('max_stamps_per_transaction')
      .single(),
    supabase
      .from('rewards')
      .select('reward_item_id, expires_at, reward_items ( name )')
      .eq('member_id', memberId)
      .eq('status', 'active'),
    supabase
      .from('reward_items')
      .select('id, name')
      .eq('active', true)
      .eq('programme', 'loyalty')
      .order('created_at', { ascending: true }),
    supabase.rpc('get_recent_activity', {
      p_member_id: memberId,
      p_page_size: 5,
    }),
  ])

  if (profileResult.error) throw profileResult.error
  if (stampCardResult.error) throw stampCardResult.error
  if (configResult.error) throw configResult.error
  if (settingsResult.error) throw settingsResult.error
  if (rewardsResult.error) throw rewardsResult.error
  if (catalogResult.error) throw catalogResult.error
  if (txResult.error) throw txResult.error

  const profile = profileResult.data
  const rpcRows = ((txResult.data as { entries: RpcActivityRow[] } | null)?.entries ?? [])

  const recentActivity: RawActivityTransaction[] = rpcRows.map((row) => {
    const snapshot = row.loyalty_config_snapshot
    return {
      id: row.id,
      type: row.type,
      stamps_delta: row.stamps_delta,
      rewards_delta: row.rewards_delta,
      created_at: row.created_at,
      cashier_name: row.actor_name ?? null,
      actor_role: row.actor_role ?? null,
      correction_reason: row.correction_reason,
      reward_item_name: row.reward_item_name
        ? (row.reward_item_name[lang] ?? row.reward_item_name.en ?? undefined)
        : undefined,
      earning_method: snapshot?.earning_method ?? null,
      amount_per_stamp: snapshot?.amount_per_stamp ?? null,
      trigger: snapshot?.trigger ?? null,
      stamp_target_before: snapshot?.stamp_target_before ?? null,
      stamp_target_after: snapshot?.stamp_target_after ?? null,
      stamps_before: snapshot?.stamps_before ?? null,
      stamps_after: snapshot?.stamps_after ?? null,
    }
  })

  const rawRewards = (rewardsResult.data ?? []) as { reward_item_id: string | null; expires_at: string; reward_items: { name: unknown } | { name: unknown }[] | null }[]
  const rewardCounts = new Map<string, { name: string; count: number; earliestExpiry: string | null }>()
  for (const r of rawRewards) {
    const key = r.reward_item_id ?? 'unknown'
    const existing = rewardCounts.get(key)
    if (existing) {
      existing.count += 1
      if (r.expires_at && (!existing.earliestExpiry || r.expires_at < existing.earliestExpiry)) {
        existing.earliestExpiry = r.expires_at
      }
    } else {
      const item = resolveFirst(r.reward_items)
      const nameObj = item?.name as Record<string, string> | undefined
      const name = nameObj?.[lang] || nameObj?.en || key
      rewardCounts.set(key, { name, count: 1, earliestExpiry: r.expires_at ?? null })
    }
  }
  const rewardBalance: MemberRewardBalance[] = Array.from(rewardCounts.entries()).map(
    ([rewardItemId, { name, count, earliestExpiry }]) => ({ rewardItemId, name, count, earliestExpiry }),
  )

  const rewardCatalog: CatalogRewardItem[] = (catalogResult.data ?? []).map((item) => {
    const nameObj = item.name as Record<string, string>
    return { id: item.id, name: nameObj[lang] || nameObj.en || item.id }
  })

  return {
    id: profile.id,
    firstName: profile.first_name,
    lastName: profile.last_name,
    email: profile.email ?? '',
    createdAt: profile.created_at,
    currentStamps: stampCardResult.data.current_stamps,
    stampTarget: configResult.data.stamp_target,
    maxStampsPerTransaction: settingsResult.data.max_stamps_per_transaction,
    completionCardIcon: configResult.data.completion_card_icon as CardIconKey,
    milestones: configResult.data.milestones as StampMilestone[],
    rewardBalance,
    rewardCatalog,
    recentActivity,
  }
}

export function useAdminMemberDetail(memberId: string | undefined, lang: string) {
  return useQuery({
    queryKey: ['admin-member-detail', memberId],
    queryFn: () => fetchMemberDetail(memberId!, lang),
    enabled: !!memberId,
    staleTime: 30 * 1000,
  })
}